import React, { useEffect, useState } from 'react';
import { Timer, Plus, Trash2 } from 'lucide-react';
import { useAuth } from '../packages/auth-client';
import {
  listTimeEntries,
  logTime,
  deleteTimeEntry,
  type TimeEntry,
} from '../packages/api-client/timetracking-api';
import { TaskTimer } from './TaskTimer';
import { formatDuration } from '../utils/duration';

interface Props {
  taskId: string;
  estimatedHours?: number | null;
}

function entryUserName(e: TimeEntry): string {
  const full = [e.user_first_name, e.user_last_name].filter(Boolean).join(' ');
  return full || e.user_email || '?';
}

export function TimeLog({ taskId, estimatedHours }: Props) {
  const { user } = useAuth();
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [totalHours, setTotalHours] = useState(0);
  const [loading, setLoading] = useState(true);

  // Form state
  const [hours, setHours] = useState('');
  const [note, setNote] = useState('');
  const [loggedAt, setLoggedAt] = useState(new Date().toISOString().split('T')[0]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    listTimeEntries(taskId)
      .then(resp => { setEntries(resp.data); setTotalHours(resp.total_hours); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [taskId]);

  async function handleLog(e: React.FormEvent) {
    e.preventDefault();
    const h = parseFloat(hours);
    if (!h || h <= 0) return;
    setSubmitting(true);
    try {
      const entry = await logTime(taskId, { hours: h, note: note.trim(), logged_at: loggedAt });
      setEntries(prev => [entry, ...prev]);
      setTotalHours(prev => prev + h);
      setHours('');
      setNote('');
    } finally { setSubmitting(false); }
  }

  function handleTimerStopped(entry: TimeEntry) {
    setEntries(prev => [entry, ...prev]);
    setTotalHours(prev => prev + entry.hours);
  }

  async function handleDelete(entry: TimeEntry) {
    try {
      await deleteTimeEntry(taskId, entry.id);
      setEntries(prev => prev.filter(e => e.id !== entry.id));
      setTotalHours(prev => Math.max(0, prev - entry.hours));
    } catch { /* ignore */ }
  }

  const pct = estimatedHours && estimatedHours > 0
    ? Math.min(100, (totalHours / estimatedHours) * 100)
    : null;

  return (
    <div className="time-log-card">
      <div className="time-log-header">
        <span style={{ fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' }}><Timer size={16} aria-hidden="true" /> Time Log</span>
        <span className="time-log-total">
          Logged: <strong>{totalHours.toFixed(1)}h</strong>
          {estimatedHours ? ` of ${estimatedHours}h estimated` : ''}
        </span>
      </div>

      {/* Live timer */}
      <TaskTimer taskId={taskId} onStopped={handleTimerStopped} />

      {/* Progress bar */}
      {pct !== null && (
        <div className="time-progress-track">
          <div
            className="time-progress-bar"
            style={{
              width: `${pct}%`,
              background: pct >= 100 ? 'var(--color-danger)' : pct >= 80 ? 'var(--color-warning)' : 'var(--color-primary)',
            }}
          />
        </div>
      )}

      {/* Log form */}
      <form onSubmit={handleLog} className="time-log-form">
        <input
          type="number"
          min={0.1}
          step={0.25}
          placeholder="Hours"
          className="form-input time-log-hours-input"
          value={hours}
          onChange={e => setHours(e.target.value)}
          required
        />
        <input
          type="text"
          placeholder="Note (optional)"
          className="form-input time-log-note-input"
          value={note}
          onChange={e => setNote(e.target.value)}
        />
        <input
          type="date"
          className="form-input time-log-date-input"
          value={loggedAt}
          onChange={e => setLoggedAt(e.target.value)}
        />
        <button className="btn btn-primary btn-sm" type="submit" disabled={submitting} style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          {submitting ? '…' : (<><Plus size={14} aria-hidden="true" /> Log</>)}
        </button>
      </form>

      {/* Entries */}
      {loading && <div style={{ textAlign: 'center', padding: '1rem', color: 'var(--color-text-muted)' }}>Loading…</div>}

      {!loading && entries.length === 0 && (
        <div className="time-log-empty">No time logged yet. Use the form above to log your first entry.</div>
      )}

      {!loading && entries.length > 0 && (
        <div className="time-entry-list">
          {entries.map(entry => (
            <div key={entry.id} className="time-entry-row">
              <span className="time-entry-avatar">
                {(entry.user_first_name?.[0] ?? entry.user_email?.[0] ?? '?').toUpperCase()}
              </span>
              <span className="time-entry-info">
                <span className="time-entry-user">{entryUserName(entry)}</span>
                {entry.note && <span className="time-entry-note">— {entry.note}</span>}
                <span className="time-entry-date">{entry.logged_at}</span>
              </span>
              <span className="time-entry-hours">+{formatDuration(entry.seconds ?? Math.round(entry.hours * 3600))}</span>
              {entry.user_id === user?.user_id && (
                <button
                  className="btn btn-ghost btn-sm"
                  style={{ color: 'var(--color-danger)', padding: '2px 6px', display: 'inline-flex', alignItems: 'center' }}
                  onClick={() => handleDelete(entry)}
                  title="Delete entry"
                  aria-label="Delete entry"
                >
                  <Trash2 size={14} aria-hidden="true" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
